import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { 
  CheckCircle, AlertCircle, Clock, FileText, Users, Send, 
  Calendar, Mic, ChevronDown, ChevronUp
} from 'lucide-react';
import { format, parseISO, differenceInDays, isPast, addDays } from 'date-fns';

const MeetingChecklist = ({ meeting, body, agendaItems, attendance, onNavigate }) => {
  const [expanded, setExpanded] = useState(true);
  
  // Calculate checklist items status
  const meetingDate = meeting?.date_time ? parseISO(meeting.date_time) : null;
  const noticeDays = body?.notice_days || 5;
  const noticeDeadline = meetingDate ? addDays(meetingDate, -noticeDays) : null;
  const daysUntilMeeting = meetingDate ? differenceInDays(meetingDate, new Date()) : 0;
  
  // Checklist items
  const checklistItems = [
    {
      id: 'notice',
      category: 'Before the Meeting',
      title: `Send notice at least ${noticeDays} days in advance`,
      description: noticeDeadline 
        ? `Notice deadline: ${format(noticeDeadline, 'MMM d, yyyy')}`
        : 'Set meeting date first',
      completed: meeting?.status === 'notice_sent' || meeting?.status === 'in_progress' || meeting?.status === 'completed',
      urgent: noticeDeadline && !isPast(noticeDeadline) && differenceInDays(noticeDeadline, new Date()) <= 2,
      overdue: noticeDeadline && isPast(noticeDeadline) && meeting?.status === 'scheduled',
      action: () => onNavigate('notices'),
      actionLabel: 'Send Notices',
      icon: Send
    },
    {
      id: 'agenda',
      category: 'Before the Meeting',
      title: 'Prepare and distribute agenda with supporting documents',
      description: agendaItems.length > 0 
        ? `${agendaItems.length} agenda items prepared`
        : 'No agenda items yet',
      completed: agendaItems.length >= 3,
      action: () => onNavigate('agenda'),
      actionLabel: 'Edit Agenda',
      icon: FileText
    },
    {
      id: 'quorum',
      category: 'Before the Meeting',
      title: 'Confirm quorum requirements',
      description: body?.quorum_config 
        ? `Quorum: ${body.quorum_config.value}${body.quorum_config.type === 'percentage' ? '%' : ''} required`
        : 'Configure quorum in admin',
      completed: !!body?.quorum_config,
      action: () => onNavigate('admin'),
      actionLabel: 'Configure',
      icon: Users
    },
    {
      id: 'previous_minutes',
      category: 'Before the Meeting',
      title: 'Prepare previous meeting minutes for approval',
      description: 'Include approval of previous minutes in agenda',
      completed: agendaItems.some(item => 
        item.title.toLowerCase().includes('previous') && 
        item.title.toLowerCase().includes('minutes')
      ),
      action: () => onNavigate('agenda'),
      actionLabel: 'Add to Agenda',
      icon: FileText
    },
    {
      id: 'attendance_ready',
      category: 'During the Meeting',
      title: 'Record attendance and time started',
      description: meeting?.status === 'in_progress' || meeting?.status === 'completed'
        ? `${attendance.filter(a => a.status?.includes('present')).length} attendees recorded`
        : 'Will be tracked in Live Console',
      completed: meeting?.status === 'completed' && attendance.length > 0,
      action: () => onNavigate('console'),
      actionLabel: 'Live Console',
      icon: Users
    },
    {
      id: 'motions',
      category: 'During the Meeting',
      title: 'Note all motions made and seconded',
      description: 'Capture mover, seconder, and motion text',
      completed: meeting?.status === 'completed' && agendaItems.some(item => item.mover && item.seconder),
      action: () => onNavigate('console'),
      actionLabel: 'Live Console',
      icon: FileText
    },
    {
      id: 'voting',
      category: 'During the Meeting',
      title: 'Document voting results',
      description: 'Record votes for/against/abstain',
      completed: meeting?.status === 'completed' && agendaItems.some(item => 
        item.votes_for > 0 || item.votes_against > 0
      ),
      action: () => onNavigate('console'),
      actionLabel: 'Live Console',
      icon: CheckCircle
    },
    {
      id: 'action_items',
      category: 'During the Meeting',
      title: 'Record action items with responsible persons',
      description: 'Assign owners and due dates',
      completed: meeting?.status === 'completed',
      action: () => onNavigate('console'),
      actionLabel: 'Live Console',
      icon: CheckCircle
    },
    {
      id: 'draft_minutes',
      category: 'After the Meeting',
      title: 'Draft minutes within 48 hours',
      description: meeting?.status === 'completed' 
        ? 'Meeting completed - draft minutes now'
        : 'Available after meeting ends',
      completed: false, // Would check if minutes exist
      action: () => onNavigate('minutes'),
      actionLabel: 'Draft Minutes',
      icon: FileText,
      disabled: meeting?.status !== 'completed'
    },
    {
      id: 'review_sign',
      category: 'After the Meeting',
      title: 'Have chairperson review and sign',
      description: 'Submit for review and approval',
      completed: false,
      action: () => onNavigate('minutes'),
      actionLabel: 'Review Process',
      icon: CheckCircle,
      disabled: meeting?.status !== 'completed'
    },
    {
      id: 'distribute',
      category: 'After the Meeting',
      title: 'Distribute to board members',
      description: 'Share approved minutes',
      completed: false,
      action: () => onNavigate('minutes'),
      actionLabel: 'Distribute',
      icon: Send,
      disabled: meeting?.status !== 'completed'
    },
    {
      id: 'file',
      category: 'After the Meeting',
      title: 'File properly for future reference',
      description: 'Store in Minutes Book',
      completed: false,
      action: () => onNavigate('records'),
      actionLabel: 'Records',
      icon: FileText,
      disabled: meeting?.status !== 'completed'
    }
  ];

  // Group by category
  const categories = ['Before the Meeting', 'During the Meeting', 'After the Meeting'];
  const groupedItems = categories.map(cat => ({
    name: cat,
    items: checklistItems.filter(item => item.category === cat)
  }));

  // Calculate progress
  const completedCount = checklistItems.filter(item => item.completed).length;
  const progressPercent = Math.round((completedCount / checklistItems.length) * 100);

  const getItemStatus = (item) => {
    if (item.completed) return 'completed';
    if (item.overdue) return 'overdue';
    if (item.urgent) return 'urgent';
    if (item.disabled) return 'disabled';
    return 'pending';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'overdue':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'urgent':
        return <Clock className="h-5 w-5 text-amber-600" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-[#E2E6EA]" />;
    }
  };

  return (
    <Card className="border-[#E2E6EA]">
      <CardHeader 
        className="cursor-pointer hover:bg-[#F8F9FA] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-[#00A3A3]" />
              Meeting Checklist
            </CardTitle>
            <span className="text-sm text-[#5A6570]">
              {completedCount}/{checklistItems.length} completed
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-32">
              <Progress value={progressPercent} className="h-2" />
            </div>
            <span className="text-sm font-medium text-[#00A3A3]">{progressPercent}%</span>
            {expanded ? <ChevronUp className="h-5 w-5 text-[#5A6570]" /> : <ChevronDown className="h-5 w-5 text-[#5A6570]" />}
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="pt-0">
          {/* Meeting Timeline Alert */}
          {daysUntilMeeting > 0 && daysUntilMeeting <= 7 && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-3 ${
              daysUntilMeeting <= 2 
                ? 'bg-amber-50 border border-amber-200' 
                : 'bg-blue-50 border border-blue-200'
            }`}>
              <Calendar className={`h-5 w-5 ${daysUntilMeeting <= 2 ? 'text-amber-600' : 'text-blue-600'}`} />
              <div>
                <p className={`font-medium ${daysUntilMeeting <= 2 ? 'text-amber-800' : 'text-blue-800'}`}>
                  {daysUntilMeeting === 1 ? 'Meeting is tomorrow!' : `${daysUntilMeeting} days until meeting`}
                </p>
                <p className="text-sm text-[#5A6570]">
                  {format(meetingDate, 'EEEE, MMMM d, yyyy h:mm a')}
                </p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {groupedItems.map((group) => (
              <div key={group.name}>
                <h4 className="text-sm font-semibold text-[#1A1D21] mb-3 flex items-center gap-2">
                  {group.name === 'Before the Meeting' && <Calendar className="h-4 w-4 text-[#00A3A3]" />}
                  {group.name === 'During the Meeting' && <Mic className="h-4 w-4 text-[#F58A26]" />}
                  {group.name === 'After the Meeting' && <FileText className="h-4 w-4 text-[#00A3A3]" />}
                  {group.name}
                </h4>
                <div className="space-y-2">
                  {group.items.map((item) => {
                    const status = getItemStatus(item);
                    return (
                      <div 
                        key={item.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                          status === 'completed' 
                            ? 'bg-green-50/50 border-green-200' 
                            : status === 'overdue'
                            ? 'bg-red-50 border-red-200'
                            : status === 'urgent'
                            ? 'bg-amber-50 border-amber-200'
                            : status === 'disabled'
                            ? 'bg-gray-50 border-gray-200 opacity-60'
                            : 'bg-white border-[#E2E6EA] hover:border-[#00A3A3]'
                        }`}
                      >
                        <div className="mt-0.5">
                          {getStatusIcon(status)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm ${
                            status === 'completed' ? 'text-green-800 line-through' : 'text-[#1A1D21]'
                          }`}>
                            {item.title}
                          </p>
                          <p className={`text-xs mt-0.5 ${
                            status === 'overdue' ? 'text-red-600' : 'text-[#5A6570]'
                          }`}>
                            {item.description}
                            {status === 'overdue' && ' - OVERDUE'}
                          </p>
                        </div>
                        {item.action && !item.disabled && status !== 'completed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => item.action()}
                            className="shrink-0 h-8 text-xs border-[#E2E6EA] hover:border-[#00A3A3] hover:bg-[#00A3A3] hover:text-white"
                          >
                            {item.actionLabel}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

        </CardContent>
      )}
    </Card>
  );
};

export default MeetingChecklist;
